import { useEffect, useState } from 'react';
import {
  SCENARIO_KINDS,
  SENIORITIES,
  dealScenarioDetailSchema,
  toWireDate,
  type ScenarioCost,
  type ScenarioKind,
  type ScenarioResource,
  type Seniority,
} from '@forecastify/shared';
import { Button, Dialog, Select, Stepper, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';
import { seniorityLabel } from './deal-kit';
import { amountText, parseAmount } from './plan-kit';
import { scenarioKindLabel, type ScenarioSend, type ScenarioWrite } from './scenario-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · the scenario screens' dialogs.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A new scenario (`07e`), the winner's confirmation (DS-1 — it replaces the plan, so it says so),
 * a resource typed by hand or edited (DS-4), and an external cost.
 */

function Footer({
  error,
  busy,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  readonly error: string | null;
  readonly busy: boolean;
  readonly confirmLabel?: string;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const t = useT();
  return (
    <>
      {error === null ? null : (
        <div className="text-destructive text-[12px]" role="alert">
          {error}
        </div>
      )}
      <div className="mt-1 flex items-center justify-end gap-2">
        <Button variant="outline" size="lg" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button size="lg" disabled={busy} onClick={onConfirm}>
          {confirmLabel ?? t('common.save')}
        </Button>
      </div>
    </>
  );
}

/** `07e` · a name, a kind and the first month. Answers with the new scenario's id. */
export function NewScenarioDialog({
  open,
  call,
  onClose,
  onCreated,
}: {
  readonly open: boolean;
  readonly call: (rest: string, method?: string, body?: unknown) => Promise<Response | string>;
  readonly onClose: () => void;
  readonly onCreated: (id: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ScenarioKind>('one_off');
  const [startsOn, setStartsOn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setKind('one_off');
    setStartsOn(`${toWireDate(new Date()).slice(0, 7)}-01`);
    setError(null);
  }, [open]);

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(t('dealScenario.error.name'));
      return;
    }
    if (startsOn === '') {
      setError(t('dealScenario.error.start'));
      return;
    }
    setBusy(true);
    const answer = await call('', 'POST', { name: name.trim(), kind, startsOn });
    setBusy(false);
    if (typeof answer === 'string') {
      setError(answer);
      return;
    }
    const parsed = dealScenarioDetailSchema.safeParse(await answer.json().catch(() => null));
    onClose();
    if (parsed.success) onCreated(parsed.data.id);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('dealScenario.new.title')}
      description={t('dealScenario.new.hint')}
      closeLabel={t('common.close')}
      width={460}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealScenario.field.name')} required>
          <TextInput
            autoFocus
            value={name}
            placeholder={t('dealScenario.field.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void submit();
            }}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dealScenario.field.kind')}>
            <Select
              value={kind}
              aria-label={t('dealScenario.field.kind')}
              onChange={(event) => {
                setKind(event.target.value as ScenarioKind);
              }}
              options={SCENARIO_KINDS.map((one) => ({
                value: one,
                label: scenarioKindLabel(t, one),
              }))}
            />
          </Field>
          <Field label={t('dealScenario.field.start')} required>
            <DateField
              value={startsOn === '' ? null : startsOn}
              onChange={setStartsOn}
              clearable={false}
              label={t('dealScenario.field.start')}
            />
          </Field>
        </div>
        <Footer
          error={error}
          busy={busy}
          confirmLabel={t('dealScenario.new.create')}
          onCancel={onClose}
          onConfirm={() => void submit()}
        />
      </div>
    </Dialog>
  );
}

/**
 * DS-1 · marking a winner REPLACES the plan — its line items, placeholders, hours and costs — and
 * drops the other scenarios. The dialog names all of it, because the plan is what the forecast,
 * the demand view and the generator read.
 */
export function WinnerDialog({
  name,
  onCancel,
  onConfirm,
}: {
  /** Null: closed. */
  readonly name: string | null;
  readonly onCancel: () => void;
  /** Resolves to the refusal's sentence, or null on success. */
  readonly onConfirm: () => Promise<string | null>;
}) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (name !== null) setError(null);
  }, [name]);

  return (
    <Dialog
      open={name !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={t('dealScenario.winner.title')}
      description={t('dealScenario.winner.body', { name: name ?? '' })}
      closeLabel={t('common.close')}
      width={480}
    >
      <div className="flex flex-col gap-3">
        <ul className="text-muted-foreground list-disc pl-5 text-[12.5px] leading-relaxed">
          <li>{t('dealScenario.winner.replace')}</li>
          <li>{t('dealScenario.winner.value')}</li>
          <li>{t('dealScenario.winner.others')}</li>
          <li>{t('dealScenario.winner.milestones')}</li>
        </ul>
        <Footer
          error={error}
          busy={busy}
          confirmLabel={t('dealScenario.winner.confirm')}
          onCancel={onCancel}
          onConfirm={() => {
            setBusy(true);
            void onConfirm().then((refusal) => {
              setBusy(false);
              setError(refusal);
            });
          }}
        />
      </div>
    </Dialog>
  );
}

/**
 * DS-4 · a resource typed by hand — a free role with its own name and rate — or one being edited.
 * `onSaved` hears the new resource's id when one was made, so the caller can put it on its line.
 */
export function ScenarioResourceDialog({
  open,
  resource,
  existing,
  send,
  onClose,
  onCreated,
}: {
  readonly open: boolean;
  /** Null: a new free role. */
  readonly resource: ScenarioResource | null;
  /** The scenario's resource ids before the write — the new one is whichever the answer adds. */
  readonly existing: readonly string[];
  readonly send: ScenarioSend;
  readonly onClose: () => void;
  /** The new resource's id — whichever the answer holds that the scenario did not. */
  readonly onCreated?: (id: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [seniority, setSeniority] = useState<Seniority | ''>('');
  const [rate, setRate] = useState('');
  const [headcount, setHeadcount] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(resource?.name ?? '');
    setSeniority(resource?.seniority ?? '');
    setRate(amountText(resource?.costRate ?? null));
    setHeadcount(String(resource?.headcount ?? 1));
    setError(null);
  }, [open, resource]);

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(t('dealScenario.error.resourceName'));
      return;
    }
    const costRate = parseAmount(rate);
    if (costRate === null) {
      setError(t('deals.error.value'));
      return;
    }
    const count = Number(headcount);
    if (!Number.isInteger(count) || count < 1 || count > 50) {
      setError(t('dealScenario.error.headcount'));
      return;
    }
    setBusy(true);
    const body = {
      name: name.trim(),
      seniority: seniority === '' ? null : seniority,
      costRate,
      headcount: count,
      ...(resource === null ? {} : { version: resource.version }),
    };
    const answer =
      resource === null
        ? await send('/resources', 'POST', body)
        : await send(`/resources/${resource.id}`, 'PATCH', body);
    setBusy(false);
    if (typeof answer === 'string') {
      setError(answer);
      return;
    }
    if (resource === null && answer !== null) {
      const made = answer.resources.find((one) => !existing.includes(one.id));
      if (made !== undefined) onCreated?.(made.id);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={resource === null ? t('dealScenario.resource.new') : t('dealScenario.resource.edit')}
      {...(resource === null ? { description: t('dealScenario.resource.newHint') } : {})}
      closeLabel={t('common.close')}
      width={460}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealScenario.resource.name')} required>
          <TextInput
            autoFocus
            value={name}
            placeholder={t('dealScenario.resource.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('dealScenario.resource.seniority')}>
            <Select
              value={seniority}
              aria-label={t('dealScenario.resource.seniority')}
              onChange={(event) => {
                setSeniority(event.target.value as Seniority | '');
              }}
              options={[
                { value: '', label: t('dealScenario.resource.noSeniority') },
                ...SENIORITIES.map((one) => ({ value: one, label: seniorityLabel(t, one) })),
              ]}
            />
          </Field>
          <Field label={t('dealScenario.resource.rate.label')}>
            <TextInput
              inputMode="decimal"
              className="tabular-nums"
              value={rate}
              placeholder="0"
              onChange={(event) => {
                setRate(event.target.value);
                setError(null);
              }}
            />
          </Field>
          <Field label={t('dealScenario.resource.headcount')}>
            <Stepper
              value={headcount}
              min={1}
              max={50}
              aria-label={t('dealScenario.resource.headcount')}
              decrementLabel={t('dealScenario.resource.headcountLess')}
              incrementLabel={t('dealScenario.resource.headcountMore')}
              onChange={(value) => {
                setHeadcount(value);
                setError(null);
              }}
            />
          </Field>
        </div>
        <Footer error={error} busy={busy} onCancel={onClose} onConfirm={() => void submit()} />
      </div>
    </Dialog>
  );
}

/** An external cost — a name, an amount and an optional date. Counted against the margin. */
export function ScenarioCostDialog({
  open,
  cost,
  write,
  onClose,
}: {
  readonly open: boolean;
  /** Null: a new cost. */
  readonly cost: ScenarioCost | null;
  readonly write: ScenarioWrite;
  readonly onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [incurredOn, setIncurredOn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(cost?.name ?? '');
    setAmount(amountText(cost?.amount ?? null));
    setIncurredOn(cost?.incurredOn ?? '');
    setError(null);
  }, [open, cost]);

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(t('dealScenario.error.costName'));
      return;
    }
    const value = parseAmount(amount);
    if (value === null) {
      setError(t('deals.error.value'));
      return;
    }
    setBusy(true);
    const body = {
      name: name.trim(),
      amount: value,
      incurredOn: incurredOn === '' ? null : incurredOn,
      ...(cost === null ? {} : { version: cost.version }),
    };
    const refusal =
      cost === null
        ? await write('/costs', 'POST', body)
        : await write(`/costs/${cost.id}`, 'PUT', body);
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={cost === null ? t('dealScenario.cost.new') : t('dealScenario.cost.edit')}
      description={t('dealScenario.cost.hint')}
      closeLabel={t('common.close')}
      width={460}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealScenario.cost.name')} required>
          <TextInput
            autoFocus
            value={name}
            placeholder={t('dealScenario.cost.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dealScenario.cost.amount')}>
            <TextInput
              inputMode="decimal"
              className="tabular-nums"
              value={amount}
              placeholder="0"
              onChange={(event) => {
                setAmount(event.target.value);
                setError(null);
              }}
            />
          </Field>
          <Field label={t('dealScenario.cost.date')}>
            <DateField
              value={incurredOn === '' ? null : incurredOn}
              onChange={setIncurredOn}
              label={t('dealScenario.cost.date')}
            />
          </Field>
        </div>
        <Footer error={error} busy={busy} onCancel={onClose} onConfirm={() => void submit()} />
      </div>
    </Dialog>
  );
}
